"use client";

import {
  type InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  createSocialFeedComment,
  deleteSocialFeedComment,
  listSocialFeedCommentLikes,
  listSocialFeedComments,
  listSocialFeedLikes,
  type SocialFeedCommentItem,
  type SocialFeedCommentLikeListResponse,
  type SocialFeedCommentListResponse,
  type SocialFeedLikeListResponse,
  setSocialFeedCommentPinned,
  toggleSocialFeedCommentLike,
  toggleSocialFeedLike,
} from "@/actions/social-feed-interaction";
import type { SocialFeedEntity } from "@/db/schema/social-feed";
import { useBotReplyCacheSync } from "@/hooks/use-bot-reply-cache-sync";
import {
  type CommentThreadPosition,
  createOptimisticCommentId,
  deriveOptimisticCommentThreadPosition,
  getCommentSubtreeIds,
} from "@/lib/comments/threading";
import { botReplyRefetchInterval } from "@/lib/hiive-bot/polling";
import {
  type MusicAttachment,
  musicAttachmentInput,
} from "@/lib/music/attachment";
import type { CommentMedia, CommentVoiceNote } from "@/validations/comment";

type CommentViewerInfo = {
  id?: string;
  displayName?: string | null;
  username?: string | null;
  avatarUrl?: string | null;
};

type CreateSocialFeedCommentPayload = {
  entityType: SocialFeedEntity;
  entityId: number;
  content: string;
  parentCommentId?: number | null;
  voice?: CommentVoiceNote | null;
  media?: CommentMedia | null;
  viewer?: CommentViewerInfo;
  /** A song reply: the full snapshot paints the optimistic card; only its id goes up. */
  song?: MusicAttachment | null;
};

type CommentMeta = {
  tempId?: string;
  isPending?: boolean;
};

type CommentsPage = Omit<SocialFeedCommentListResponse, "comments"> & {
  comments: Array<SocialFeedCommentItem & CommentMeta>;
};

type CommentsInfiniteData = InfiniteData<CommentsPage, number | null>;

type ToggleCommentLikeVariables = {
  commentId: number;
  entityType: SocialFeedEntity;
  entityId: number;
  currentlyLiked: boolean;
};

type ToggleSocialFeedLikeVariables = {
  entityType: SocialFeedEntity;
  entityId: number;
  currentlyLiked: boolean;
};

export const socialFeedInteractionKeys = {
  likes: (entityType: SocialFeedEntity, entityId: number) =>
    ["socialFeed", "likes", entityType, entityId] as const,
  comments: (entityType: SocialFeedEntity, entityId: number) =>
    ["socialFeed", "comments", entityType, entityId] as const,
  commentLikes: (commentId: number) =>
    ["socialFeed", "comment-likes", commentId] as const,
};

export function useSocialFeedLikes(
  entityType: SocialFeedEntity | null,
  entityId: number | null,
  options?: {
    enabled?: boolean;
    limit?: number;
  },
) {
  const { enabled = true, limit = 30 } = options ?? {};

  return useInfiniteQuery({
    queryKey: socialFeedInteractionKeys.likes(
      entityType ?? "hiive_membership",
      entityId ?? 0,
    ),
    enabled: Boolean(entityType && entityId && enabled),
    queryFn: async ({ pageParam }) => {
      if (!entityType || !entityId) {
        return {
          likes: [],
          hasMore: false,
          nextCursor: null,
        } satisfies SocialFeedLikeListResponse;
      }

      const result = await listSocialFeedLikes({
        entityType,
        entityId,
        cursor: pageParam ?? undefined,
        limit,
      });

      if (!result.ok) {
        throw new Error(result.message ?? "Failed to fetch likes");
      }

      return result.data;
    },
    initialPageParam: null as SocialFeedLikeListResponse["nextCursor"],
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextCursor : undefined,
    staleTime: 15_000,
    // Other people change this list, and notifications link straight into
    // it; with `false` the 24h cache served it stale until an app restart.
    refetchOnMount: true,
    placeholderData: keepPreviousData,
  });
}

export function useToggleSocialFeedLikeMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["socialFeed", "toggle-like"],
    mutationFn: async ({
      entityType,
      entityId,
    }: ToggleSocialFeedLikeVariables) => {
      const result = await toggleSocialFeedLike({ entityType, entityId });
      if (!result.ok) {
        throw new Error(result.message ?? "Failed to toggle like");
      }
      return { liked: result.data.liked };
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: socialFeedInteractionKeys.likes(
          variables.entityType,
          variables.entityId,
        ),
      });
      queryClient.invalidateQueries({ queryKey: ["socialFeed", "feed"] });
    },
  });
}

export function useSocialFeedComments(
  entityType: SocialFeedEntity | null,
  entityId: number | null,
  options?: {
    enabled?: boolean;
    limit?: number;
  },
) {
  const { enabled = true, limit = 100 } = options ?? {};

  const query = useInfiniteQuery({
    queryKey: socialFeedInteractionKeys.comments(
      entityType ?? "hiive_membership",
      entityId ?? 0,
    ),
    enabled: Boolean(entityType && entityId && enabled),
    queryFn: async ({ pageParam }) => {
      if (!entityType || !entityId) {
        return {
          comments: [],
          hasMore: false,
          nextCursor: null,
        } satisfies CommentsPage;
      }

      const result = await listSocialFeedComments({
        entityType,
        entityId,
        cursor: pageParam ?? undefined,
        limit,
      });

      if (!result.ok) {
        throw new Error(result.message ?? "Failed to fetch comments");
      }

      return {
        ...result.data,
        comments: result.data.comments.map((comment) => ({
          ...comment,
          isPending: false,
        })),
      } satisfies CommentsPage;
    },
    initialPageParam: null as number | null,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextCursor : undefined,
    refetchInterval: (query) =>
      botReplyRefetchInterval(
        query.state.data?.pages.flatMap((page) => page.comments) ?? [],
      ),
    staleTime: 15_000,
    // Other people change this list, and notifications link straight into
    // it; with `false` the 24h cache served it stale until an app restart.
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
    refetchIntervalInBackground: false,
  });
  useBotReplyCacheSync(
    `social:${entityType}:${entityId}`,
    query.data?.pages.flatMap((page) => page.comments) ?? [],
    () => {
      if (entityType && entityId && enabled) void query.refetch();
    },
  );
  return query;
}

export function useCreateSocialFeedCommentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["socialFeed", "comments", "create"],
    mutationFn: async (payload: CreateSocialFeedCommentPayload) => {
      const result = await createSocialFeedComment({
        entityType: payload.entityType,
        entityId: payload.entityId,
        content: payload.content,
        parentCommentId: payload.parentCommentId ?? null,
        voice: payload.voice ?? null,
        media: payload.media ?? null,
        music: payload.song ? musicAttachmentInput(payload.song) : null,
      });
      if (!result.ok) {
        throw new Error(result.message ?? "Failed to create comment");
      }
      return { id: result.data.id, payload };
    },
    onMutate: async (payload) => {
      const key = socialFeedInteractionKeys.comments(
        payload.entityType,
        payload.entityId,
      );
      await queryClient.cancelQueries({ queryKey: key });

      const prev = queryClient.getQueryData<CommentsInfiniteData>(key);
      const threadPosition = deriveOptimisticCommentThreadPosition(
        payload.parentCommentId,
        prev?.pages,
      );
      const optimistic = threadPosition
        ? buildOptimisticSocialFeedComment(payload, threadPosition)
        : null;

      if (optimistic) {
        queryClient.setQueryData<CommentsInfiniteData>(key, (old) =>
          prependComment(old, optimistic),
        );
      }

      return {
        prev,
        optimisticId: optimistic?.tempId,
        entityType: payload.entityType,
        entityId: payload.entityId,
      };
    },
    onError: (_err, payload, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(
          socialFeedInteractionKeys.comments(
            payload.entityType,
            payload.entityId,
          ),
          ctx.prev,
        );
      }
    },
    onSuccess: (result, payload, ctx) => {
      const key = socialFeedInteractionKeys.comments(
        payload.entityType,
        payload.entityId,
      );
      queryClient.setQueryData<CommentsInfiniteData>(key, (old) =>
        replaceCommentId(old, ctx?.optimisticId, result.id),
      );
    },
    onSettled: (_data, _error, payload) => {
      queryClient.invalidateQueries({
        queryKey: socialFeedInteractionKeys.comments(
          payload.entityType,
          payload.entityId,
        ),
      });
      queryClient.invalidateQueries({ queryKey: ["socialFeed", "feed"] });
    },
  });
}

export function useDeleteSocialFeedCommentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["socialFeed", "comments", "delete"],
    mutationFn: async ({
      id,
    }: {
      id: number;
      entityType: SocialFeedEntity;
      entityId: number;
    }) => {
      const result = await deleteSocialFeedComment({ id });
      if (!result.ok) {
        throw new Error(result.message ?? "Failed to delete comment");
      }
      return { id };
    },
    onMutate: async ({
      id,
      entityType,
      entityId,
    }: {
      id: number;
      entityType: SocialFeedEntity;
      entityId: number;
    }) => {
      const key = socialFeedInteractionKeys.comments(entityType, entityId);
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<CommentsInfiniteData>(key);

      queryClient.setQueryData<CommentsInfiniteData>(key, (old) =>
        removeComment(old, id),
      );

      return { prev, entityType, entityId };
    },
    onError: (_err, variables, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(
          socialFeedInteractionKeys.comments(
            variables.entityType,
            variables.entityId,
          ),
          ctx.prev,
        );
      }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: socialFeedInteractionKeys.comments(
          variables.entityType,
          variables.entityId,
        ),
      });
      queryClient.invalidateQueries({ queryKey: ["socialFeed", "feed"] });
    },
  });
}

export function useToggleSocialFeedCommentLikeMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["socialFeed", "comments", "toggle-like"],
    mutationFn: async ({ commentId }: ToggleCommentLikeVariables) => {
      const result = await toggleSocialFeedCommentLike({ commentId });
      if (!result.ok) {
        throw new Error(result.message ?? "Failed to toggle like");
      }
      return { liked: result.data.liked };
    },
    onMutate: async ({
      commentId,
      entityType,
      entityId,
      currentlyLiked,
    }: ToggleCommentLikeVariables) => {
      const key = socialFeedInteractionKeys.comments(entityType, entityId);
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<CommentsInfiniteData>(key);

      queryClient.setQueryData<CommentsInfiniteData>(key, (old) =>
        updateComment(old, commentId, (comment) => ({
          ...comment,
          isLikedByViewer: !currentlyLiked,
          likeCount: Math.max(0, comment.likeCount + (currentlyLiked ? -1 : 1)),
        })),
      );

      return { prev, entityType, entityId };
    },
    onError: (_err, variables, ctx) => {
      if (!ctx?.prev) return;
      queryClient.setQueryData(
        socialFeedInteractionKeys.comments(
          variables.entityType,
          variables.entityId,
        ),
        ctx.prev,
      );
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: socialFeedInteractionKeys.comments(
          variables.entityType,
          variables.entityId,
        ),
      });
    },
  });
}

export function useSetSocialCommentPinnedMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["socialFeed", "comments", "pin"],
    mutationFn: async (input: {
      entityType: SocialFeedEntity;
      entityId: number;
      commentId: number;
      isPinned: boolean;
    }) => {
      const result = await setSocialFeedCommentPinned(input);
      if (!result.ok) throw new Error(result.message);
      return result.data;
    },
    onSettled: (_data, _error, input) => {
      queryClient.invalidateQueries({
        queryKey: socialFeedInteractionKeys.comments(
          input.entityType,
          input.entityId,
        ),
      });
    },
  });
}

type SocialFeedCommentLikesPage = SocialFeedCommentLikeListResponse;

export function useSocialFeedCommentLikes(
  commentId: number | null,
  options?: {
    enabled?: boolean;
    limit?: number;
  },
) {
  const { enabled = true, limit = 30 } = options ?? {};

  return useInfiniteQuery({
    queryKey: socialFeedInteractionKeys.commentLikes(commentId ?? 0),
    enabled: Boolean(commentId && enabled),
    queryFn: async ({ pageParam }) => {
      if (!commentId) {
        return {
          likes: [],
          hasMore: false,
          nextCursor: null,
        } satisfies SocialFeedCommentLikesPage;
      }

      const result = await listSocialFeedCommentLikes({
        commentId,
        cursor: pageParam ?? undefined,
        limit,
      });

      if (!result.ok) {
        throw new Error(result.message ?? "Failed to fetch comment likes");
      }

      return result.data;
    },
    initialPageParam: null as SocialFeedCommentLikesPage["nextCursor"],
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextCursor : undefined,
    staleTime: 15_000,
    // Other people change this list, and notifications link straight into
    // it; with `false` the 24h cache served it stale until an app restart.
    refetchOnMount: true,
    placeholderData: keepPreviousData,
  });
}

function buildOptimisticSocialFeedComment(
  payload: CreateSocialFeedCommentPayload,
  threadPosition: CommentThreadPosition,
): SocialFeedCommentItem & CommentMeta {
  const id = createOptimisticCommentId();
  return {
    id,
    entityType: payload.entityType,
    entityId: payload.entityId,
    parentCommentId: threadPosition.parentCommentId,
    rootCommentId: threadPosition.rootCommentId,
    depth: threadPosition.depth,
    content: payload.content,
    voice: payload.voice ?? null,
    media: payload.media ?? null,
    music: payload.song ?? null,
    isPinned: false,
    pinnedAt: null,
    createdAt: new Date(),
    author: {
      id: payload.viewer?.id ?? "optimistic",
      displayName:
        payload.viewer?.displayName || payload.viewer?.username || "You",
      username: payload.viewer?.username ?? null,
      avatarUrl: payload.viewer?.avatarUrl ?? null,
    },
    likeCount: 0,
    isLikedByViewer: false,
    tempId: `temp-comment-${Math.abs(id)}`,
    isPending: true,
  };
}

function prependComment(
  data: CommentsInfiniteData | undefined,
  comment: SocialFeedCommentItem & CommentMeta,
): CommentsInfiniteData | undefined {
  if (!data) {
    return {
      pages: [
        {
          comments: [comment],
          hasMore: false,
          nextCursor: null,
        },
      ],
      pageParams: [null],
    };
  }

  const [first, ...rest] = data.pages;
  const deduped = first.comments.filter(
    (existing) =>
      (existing.tempId ?? existing.id) !== (comment.tempId ?? comment.id),
  );
  const nextFirst: CommentsPage = {
    ...first,
    comments: [comment, ...deduped],
  };

  return {
    ...data,
    pages: [nextFirst, ...rest],
  };
}

function replaceCommentId(
  data: CommentsInfiniteData | undefined,
  tempId: string | undefined,
  id: number,
): CommentsInfiniteData | undefined {
  if (!data || !tempId) return data;

  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      comments: page.comments.map((comment) => {
        if (comment.tempId !== tempId) return comment;
        return {
          ...comment,
          id,
          tempId: undefined,
          isPending: false,
        };
      }),
    })),
  };
}

function removeComment(
  data: CommentsInfiniteData | undefined,
  id: number,
): CommentsInfiniteData | undefined {
  if (!data) return data;

  const removedIds = getCommentSubtreeIds(
    data.pages.flatMap((page) => page.comments),
    id,
  );

  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      comments: page.comments.filter((comment) => !removedIds.has(comment.id)),
    })),
  };
}

function updateComment(
  data: CommentsInfiniteData | undefined,
  commentId: number,
  updater: (
    comment: SocialFeedCommentItem & CommentMeta,
  ) => SocialFeedCommentItem & CommentMeta,
): CommentsInfiniteData | undefined {
  if (!data) return data;

  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      comments: page.comments.map((comment) =>
        comment.id === commentId ? updater(comment) : comment,
      ),
    })),
  };
}
